/**
 * Форма создания заявки персоналом/менеджером («+ Заявка», канбан #34,
 * дизайн manager v3 / guest v7 request-new).
 *
 * Поля (по дизайну):
 *   - Номер (опц.) — список номеров с активным проживанием (GET
 *     /v1/rooms/calendar): выбор привязывает заявку к stay_id + гостю; без
 *     выбора — общая заявка (walk-in / без привязки к номеру);
 *   - Категория услуги (обяз.) — из каталога услуг (GET /v1/service-catalog);
 *   - Описание (обяз.) — свободный текст;
 *   - Приоритет — Обычный / Высокий / Срочный (staff может «Срочно»).
 *
 * Сабмит → POST /v1/admin/requests → заявка появляется в колонке «Новые»
 * (инвалидация канбан-списка в useCreateManagerRequest), тост успеха.
 *
 * i18n: все строки RU. Мобайл 375 — поля в одну колонку, full-width.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRoomsCalendar, getRequestTypes } from '@hsc/api-client';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { CalendarResponse, RequestTypesListResponse } from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { useCreateManagerRequest } from './useRequestsKanban.js';
import { categoryLabel } from './kanban-model.js';

/** Приоритеты, доступные персоналу при создании. */
const PRIORITIES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'normal', label: 'Обычный' },
  { value: 'high', label: 'Высокий' },
  { value: 'urgent', label: 'Срочный' },
];

/** Окно календаря «сегодня» для подбора активных проживаний. */
function todayWindow(): { from: string; to: string } {
  // Окно для подгрузки СПИСКА номеров (бэк требует from < to — нулевой период
  // даёт 400). Берём 14 дней вперёд; занятость опциональна, важен список номеров.
  const iso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 14);
  return { from: iso(from), to: iso(to) };
}

/** Опция номера. stayId/guestName заполнены, если в номере есть активный гость. */
interface RoomOption {
  roomId: string;
  number: string;
  stayId: string | undefined;
  guestName: string | undefined;
}

/** ВСЕ номера отеля → опции выбора; активное проживание (если есть) привязывает stay_id/гостя. */
function roomOptionsFrom(cal: CalendarResponse | undefined): RoomOption[] {
  if (!cal) return [];
  const out: RoomOption[] = cal.rooms.map((room) => {
    const occ = room.occupancies.find(
      (o) => o.status !== 'cancelled' && o.status !== 'checked_out',
    );
    return {
      roomId: room.id,
      number: room.number,
      stayId: occ?.stay_id,
      guestName: occ?.guest_name,
    };
  });
  out.sort((a, b) => a.number.localeCompare(b.number, 'ru', { numeric: true }));
  return out;
}

export interface CreateRequestFormProps {
  /** Закрыть форму и вернуться к доске. */
  onClose: () => void;
  /** Уведомить об успехе (тост на доске). */
  onCreated: (title: string) => void;
}

/** Форма «+ Заявка»: номер/гость · категория · описание · приоритет. */
export function CreateRequestForm({ onClose, onCreated }: CreateRequestFormProps): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const win = useMemo(() => todayWindow(), []);

  const reqTypesQ = useQuery<RequestTypesListResponse>({
    queryKey: ['service-catalog', 'request-types', locale],
    queryFn: () => getRequestTypes(locale),
  });
  const calendarQ = useQuery<CalendarResponse>({
    queryKey: ['stays', 'calendar', win.from, win.to],
    queryFn: () => getRoomsCalendar(win.from, win.to),
  });

  const roomOptions = useMemo(() => roomOptionsFrom(calendarQ.data), [calendarQ.data]);
  // Категории заявки = НАСТРОЕННЫЕ менеджером «типы заявок» (категории с
  // is_request_type=true, миграция 0068). Берём из /v1/service-catalog/
  // request-types — без эвристики isFnbCategoryCode (которая ошибочно
  // вырезала room_service/кальян). Меню (kitchen_*/напитки) сюда не попадает.
  const categories = useMemo(() => reqTypesQ.data?.request_types ?? [], [reqTypesQ.data]);

  const [roomId, setRoomId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const create = useCreateManagerRequest();

  const selectedRoom = useMemo(
    () => roomOptions.find((r) => r.roomId === roomId) ?? null,
    [roomOptions, roomId],
  );
  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) ?? null,
    [categories, categoryId],
  );

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFieldError(null);
    if (categoryId === '') {
      setFieldError('Выберите категорию услуги');
      return;
    }
    if (description.trim() === '') {
      setFieldError('Опишите, что нужно сделать');
      return;
    }
    // Заголовок собираем из категории и номера (как в карточке заявки).
    const catName = selectedCategory ? selectedCategory.name : categoryLabel('');
    const title = selectedRoom ? `${catName} · №${selectedRoom.number}` : catName;
    create.mutate(
      {
        category_id: categoryId,
        title,
        description: description.trim(),
        priority,
        ...(selectedRoom?.stayId ? { stay_id: selectedRoom.stayId } : {}),
      },
      {
        onSuccess: () => {
          onCreated(title);
          onClose();
        },
        onError: (err) =>
          setFieldError(
            err.message !== '' ? err.message : 'Не получилось создать заявку. Попробуйте ещё раз.',
          ),
      },
    );
  }

  const isLoading = reqTypesQ.isLoading || calendarQ.isLoading;

  return (
    <section data-testid="kanban-create-form">
      <button type="button" className="ta-back" onClick={onClose} data-testid="create-form-back">
        ‹ К доске
      </button>
      <h1 className="hsc-page__title">Новая заявка</h1>

      {isLoading ? (
        <Card>
          <Skeleton height={48} radius={12} />
          <div style={{ height: 12 }} />
          <Skeleton height={120} radius={12} />
        </Card>
      ) : (
        <Card>
          <form onSubmit={onSubmit} noValidate>
            {fieldError !== null && (
              <div className="ta-error-summary" role="alert" data-testid="create-form-error">
                {fieldError}
              </div>
            )}

            {/* Номер / гость — опциональная привязка к проживанию */}
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="cr-room">
                Номер · гость (опц.)
              </label>
              <select
                id="cr-room"
                className="ta-select"
                data-testid="create-form-room"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
              >
                <option value="">Без привязки к номеру</option>
                {roomOptions.map((r) => (
                  <option key={r.roomId} value={r.roomId}>
                    №{r.number}
                    {r.guestName ? ` · ${r.guestName}` : ' · свободен'}
                  </option>
                ))}
              </select>
            </div>

            {/* Категория услуги — из каталога */}
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="cr-category">
                Категория услуги
              </label>
              <select
                id="cr-category"
                className="ta-select"
                data-testid="create-form-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Выберите категорию…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Описание */}
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="cr-description">
                Что нужно сделать
              </label>
              <textarea
                id="cr-description"
                className="ta-textarea"
                rows={4}
                data-testid="create-form-description"
                placeholder="Опишите заявку — например: заменить полотенца в №203"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Приоритет */}
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="cr-priority">
                Приоритет
              </label>
              <select
                id="cr-priority"
                className="ta-select"
                data-testid="create-form-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rq-actions">
              <Button type="submit" loading={create.isPending} data-testid="create-form-submit">
                Создать заявку
              </Button>
              <Button type="button" variant="secondary" onClick={onClose}>
                Отмена
              </Button>
            </div>
          </form>
        </Card>
      )}
    </section>
  );
}
